import axios from "axios";
import { fileNameFromDisposition } from "../utils/download";
import { getItem } from "../utils/safeStorage";
import TokenService from "./token.service";
import { refreshAccessToken } from "./tokenRefresh";
import {
  MASK_NAMES_PARAM,
  maskNamesRequested,
} from "../utils/stores/useMaskNamesStore";

const baseURL = process.env.REACT_APP_API_URL;

const bilAPI = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
    accept: "application/json",
  },
  // An array in `params` goes out as a repeated key (`group=a&group=b`),
  // which is what DRF's `getlist()` reads; axios's default `group[]=a` is
  // read by nothing on the backend. Set once here so a service can pass a
  // plain object with arrays instead of hand-building a URLSearchParams -
  // which the mask-names interceptor below cannot spread.
  paramsSerializer: { indexes: null },
});

bilAPI.interceptors.request.use(
  (config) => {
    const token = TokenService.getLocalAccessToken();

    if (token) {
      config.headers["Authorization"] = "Bearer " + token;
    } else {
      config.headers["Authorization"] = null;
    }

    // Carried here rather than at each call site: every list, report and export
    // has to agree about what is hidden, and a screen that forgot to ask would
    // be the one that leaks. Safe to send blindly because the flag can only
    // mask further, never reveal. Exports read it off the same query string --
    // the server writes it into the task payload it authors.
    if (maskNamesRequested()) {
      config.params = { ...config.params, [MASK_NAMES_PARAM]: 1 };
    }

    // Read per request, not once when this module is first imported. As a
    // default on the instance it froze at whatever was stored on page load:
    // empty on a first visit (i18next writes the key during its own init), so
    // the server answered in its default language until the tab was reloaded,
    // and stale after any language switch that does not reload the page.
    const language = getItem("i18nextLng");
    if (language) {
      config.headers["Accept-Language"] = language;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Endpoints that hand out tokens. A 401 from one of these means the credential
// offered was refused, not that an access token went stale, so it must not be
// retried behind a refresh -- the refresh would fail and replace the real
// error, wiping the stored tokens on the way out.
const CREDENTIAL_ENDPOINTS = [
  "/auth/login",
  "/auth/refresh",
  "/auth/oidc/start",
  "/auth/oidc/callback",
];

const isCredentialEndpoint = (url) => {
  const path = String(url || "").split("?")[0].replace(/\/+$/, "");
  return CREDENTIAL_ENDPOINTS.includes(path);
};

bilAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalConfig = error?.config;

    // No response = network/timeout error; nothing to refresh.
    if (!error?.response || !originalConfig) return Promise.reject(error);

    // A request made with responseType "blob" (a template, a protocol, an
    // export) gets its error body as a Blob too, so the {"detail": "..."} the
    // backend sends is unreadable where every call site expects it and
    // getApiError() would render "[object Blob]". Re-hydrate it into JSON
    // here, once, for every such request. A body that is not JSON (an HTML
    // error page) is left as it came - the caller still has the status.
    const data = error.response.data;
    if (typeof Blob !== "undefined" && data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await data.text());
      } catch {
        // not JSON - keep the Blob
      }
    }

    if (
      error.response.status === 401 &&
      !originalConfig._retry &&
      !isCredentialEndpoint(originalConfig.url)
    ) {
      originalConfig._retry = true; // one retry per request, never a loop
      try {
        await refreshAccessToken();
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
      return bilAPI(originalConfig);
    }

    return Promise.reject(error);
  }
);

/**
 * A file the server builds and hands straight back: `{ blob, fileName }`, the
 * name being the one the server gave it (Content-Disposition, exposed through
 * CORS - null when absent, so the caller picks its own fallback). A 403/404
 * body still reads as JSON: the response interceptor re-hydrates blob errors.
 */
export const getFile = (url, config = {}) =>
  bilAPI.get(url, { ...config, responseType: "blob" }).then((res) => ({
    blob: res.data,
    fileName: fileNameFromDisposition(res.headers?.["content-disposition"]),
  }));

export default bilAPI;
